import { KeyboardArrowDown } from "@mui/icons-material";
import type { MouseEvent } from "react";

/**
 * GFG-272 — Reports: Top Gifts. Local to this file: it only ever crosses into
 * `TopGiftsCampaignMenu.tsx` as a `renderTrigger` return value, never as an imported type.
 */
interface TopGiftsMenuButtonProps {
  label: string;
  isActive: boolean;
  isOpen: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
  /** Spread from `ImpactFilterMenuTriggerRenderArgs` — see that type for why this is mandatory. */
  triggerAttributes: Record<string, string>;
  ariaLabel: string;
}

/**
 * The trigger shared by BOTH of this tab's controls (campaign and period), supplied to
 * `ImpactFilterMenu`/`DatePresetMenu` via `renderTrigger` instead of that component's default icon
 * + UPPERCASE word button: this label is the chosen VALUE ("All Campaigns" / a campaign name /
 * "2 campaigns" / "Last 30 days"), which reads as a phrase, not a category tag — so no
 * `uppercase`, no `letter-spacing`, unlike the Impact Report's four controls.
 *
 * Because `renderTrigger` is in play, `ImpactFilterMenu` does not apply `aria-haspopup`/
 * `aria-expanded`/an accessible name on our behalf — this button owns all three itself.
 */
export function TopGiftsMenuButton({
  label,
  isActive,
  isOpen,
  onOpen,
  triggerAttributes,
  ariaLabel,
}: TopGiftsMenuButtonProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      {...triggerAttributes}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      // The accessible name must CONTAIN the visible label (WCAG 2.5.3 Label in Name), so a
      // screen reader hears which campaign/period is active, not just the generic category.
      aria-label={`${ariaLabel}: ${label}`}
      className={`h-11 px-4 inline-flex items-center gap-2 whitespace-nowrap rounded-lg border bg-white font-lato text-[13px] text-rodeo-black transition-colors hover:border-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
        isActive ? "border-dark-dust bg-light-dust" : "border-smoke"
      }`}
    >
      {label}
      <KeyboardArrowDown className="text-[18px] text-dark-dust" />
    </button>
  );
}
